import { useState, useEffect, useCallback, useRef } from 'react'
import { useApi } from './useApi'
import { logger } from '../utils/logger'

interface AdaptivePollingOptions {
  enabled?: boolean
  activeInterval?: number
  wsInterval?: number
  backgroundInterval?: number
}

interface AdaptivePollingResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * WebSocket接続状態とページ可視性に応じて
 * ポーリング間隔を動的に調整するカスタムフック
 */
export function useAdaptivePolling<T = unknown>(
  endpoint: string,
  onSuccess: ((data: T) => void) | undefined,
  wsConnected = false,
  options: AdaptivePollingOptions = {}
): AdaptivePollingResult<T> {
  const {
    enabled = true,
    activeInterval = 5000,      // アクティブ時の間隔（WebSocket切断時）
    wsInterval = 30000,          // WebSocket接続時の間隔
    backgroundInterval = 60000,  // バックグラウンド時の間隔
  } = options

  const api = useApi()
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isVisible, setIsVisible] = useState(!document.hidden)

  // ページ可視性の監視
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // 動的なポーリング間隔を計算
  const getPollingInterval = useCallback(() => {
    // バックグラウンドでもWebSocket切断時は短い間隔を維持
    // （着信を受けられるようにするため）
    if (!isVisible && !wsConnected) return activeInterval

    // バックグラウンド＋WebSocket接続時は長い間隔でOK
    if (!isVisible && wsConnected) return backgroundInterval

    // アクティブ時
    if (wsConnected) return wsInterval
    return activeInterval
  }, [isVisible, wsConnected, activeInterval, wsInterval, backgroundInterval])

  // データ取得関数
  const fetchData = useCallback(async () => {
    if (!enabled) return

    try {
      setLoading(true)
      const result = await api.get<T>(endpoint)
      setData(result)
      setError(null)

      if (onSuccess) {
        onSuccess(result)
      }
    } catch (err) {
      logger.error(`[useAdaptivePolling] ${endpoint} エラー:`, err)
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [api, endpoint, enabled, onSuccess])

  // ポーリングの開始・停止
  useEffect(() => {
    if (!enabled) return

    const interval = getPollingInterval()

    logger.debug(`[useAdaptivePolling] ポーリング開始: ${endpoint}`, {
      interval: `${interval/1000}秒`,
      wsConnected,
      isVisible,
    })

    // 初回実行
    fetchData()

    // 定期実行
    intervalRef.current = setInterval(fetchData, interval)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        logger.debug(`[useAdaptivePolling] ポーリング停止: ${endpoint}`)
      }
    }
  }, [enabled, fetchData, getPollingInterval, endpoint, wsConnected, isVisible])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
